import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

const formControlErrorVariants = cva('text-main-foreground-danger text-body-small', {
  variants: {
    errorPosition: {
      block: 'mt-1.5',
      overlay: 'absolute left-0 top-full mt-1',
    },
  },
  defaultVariants: {
    errorPosition: 'block',
  },
})

interface FormControlProps extends VariantProps<typeof formControlErrorVariants> {
  label?: React.ReactNode
  optional?: boolean
  helperText?: React.ReactNode
  errorText?: React.ReactNode
  state?: 'default' | 'error'
  children: React.ReactNode
  htmlFor?: string
}

export const FormControl: React.FC<FormControlProps> = ({
  label,
  optional,
  helperText,
  errorText,
  state = 'default',
  children,
  htmlFor,
  errorPosition,
}) => {
  const hasError = state === 'error' || !!errorText
  const isOverlay = errorPosition === 'overlay'

  const renderLabel = () =>
    label && (
      <div className='mb-2'>
        <div className='flex items-center gap-1 text-main-foreground-primary text-label-default'>
          <label htmlFor={htmlFor} className='block'>
            {label}
          </label>
          {optional && <span className=''>(optional)</span>}
        </div>
        {helperText && !hasError && (
          <div className='my-1 text-main-foreground-tertiary text-body-small'>{helperText}</div>
        )}
      </div>
    )

  const renderError = () =>
    hasError &&
    errorText && (
      <div
        id={`${htmlFor || ''}-error`}
        className={formControlErrorVariants({ errorPosition })}
        role='alert'
      >
        {errorText}
      </div>
    )

  return (
    <div className={cn('w-full', isOverlay && 'relative min-w-0')}>
      {renderLabel()}
      {children}
      {renderError()}
    </div>
  )
}
